import { isEditableEventTarget } from '@littlebird/action-cards';
import {
  type IconColorKey,
  isAgentThread,
  isChatStatusBusy,
  type Message,
  useStores,
} from '@littlebird/sdk';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { logError } from '@/lib/errorLogging';

import { birdActivityFor } from './agents-bird.look';
import { type AgentEntry, agentsStore } from './agents.store';
import {
  isAgentPrimingMessage,
  isAgentProfileUpdateMessage,
  isPendingAgentCard,
  isTurnUsingTool,
} from './agents.utils';

export const useAgentPendingCards = () => {
  // Cards change status by sync on the same MobX store, so React Compiler must not cache this filter
  'use no memo';
  const { actionCards, threads, agents } = useStores();
  // A card dismissed during the visit stays listed, so it folds to Restore and its toast lives
  const listedCardIds = useRef(new Set<string>()).current;
  return actionCards.orderedData
    .filter((card) => {
      if (!card.chatId) return false;
      const thread = threads.getById(card.chatId);
      if (thread === undefined || !isAgentThread(thread)) return false;
      if (agents.getById(thread.agentId)?.archivedAt) return false;
      if (isPendingAgentCard(card)) {
        listedCardIds.add(card.id);
        return true;
      }
      return (
        card.status === 'dismissed' &&
        !card.deletedAt &&
        listedCardIds.has(card.id)
      );
    })
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
};

export const useArrowKeysCycleAgents = (
  agentIds: readonly string[],
  selectedAgentId: string | undefined,
  onSelect: (agentId: string | undefined) => void,
  enabled: boolean
) => {
  const stops = useMemo(() => [undefined, ...agentIds], [agentIds]);
  useEffect(() => {
    if (!enabled) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      const step =
        event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
      const hasModifier =
        event.metaKey || event.ctrlKey || event.altKey || event.shiftKey;
      if (step === 0 || hasModifier || isEditableEventTarget(event.target))
        return;
      event.preventDefault();
      const current = stops.indexOf(selectedAgentId);
      onSelect(stops[(current + step + stops.length) % stops.length]);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enabled, stops, selectedAgentId, onSelect]);
};

export const useAgentCustomize = (agent: AgentEntry, onClose: () => void) => {
  const [look, setLook] = useState(agent.bird);
  const [name, setName] = useState(agent.name);
  const [isSaving, setIsSaving] = useState(false);
  const trimmedName = name.trim();

  return {
    look,
    name,
    isSaving,
    canSave: trimmedName.length > 0,
    setName,
    pickPlumage: (plumage: IconColorKey) =>
      setLook((current) => ({ ...current, plumage })),
    save: async () => {
      setIsSaving(true);
      try {
        await agentsStore.updateAgent(agent.agentId, {
          name: trimmedName,
          iconColor: look.plumage,
        });
        onClose();
      } catch (error) {
        logError(error, 'Couldn’t save your changes to this agent', {
          context: 'AgentCustomizeDialog',
          metadata: { agentId: agent.agentId },
        });
        toast.error('Couldn’t save your changes to this agent');
        setIsSaving(false);
      }
    },
  };
};

type AgentActivityOptions = {
  agentId: string | undefined;
  chatId: string | undefined;
  introPending: boolean;
  active: boolean;
};

type AgentActivityStores = Pick<
  ReturnType<typeof useStores>,
  'threads' | 'messages'
>;

const isVisibleTurn = (message: Message) =>
  (message.role === 'user' || message.role === 'assistant') &&
  (message.content ?? '').trim().length > 0 &&
  !isAgentPrimingMessage(message) &&
  !isAgentProfileUpdateMessage(message);

const findLastVisibleMessage = (messages: Message[]) => {
  for (let index = messages.length - 1; index >= 0; index--) {
    if (isVisibleTurn(messages[index])) return messages[index];
  }
  return undefined;
};

export const readAgentActivity = (
  { threads, messages }: AgentActivityStores,
  { agentId, chatId, introPending, active }: AgentActivityOptions
) => {
  const threadMessages = chatId ? messages.getMessagesByThreadId(chatId) : [];
  const lastVisibleMessage = findLastVisibleMessage(threadMessages);
  const thread = chatId ? threads.get(chatId) : undefined;
  const isStarting =
    introPending && chatId !== undefined && lastVisibleMessage === undefined;
  const lastMessageIso = lastVisibleMessage
    ? new Date(lastVisibleMessage.updatedAt).toISOString()
    : undefined;
  const lastViewedAt = agentId ? agentsStore.lastViewedAt(agentId) : undefined;
  const isUnread =
    !active &&
    lastVisibleMessage?.role === 'assistant' &&
    lastMessageIso !== undefined &&
    (lastViewedAt === undefined || lastMessageIso > lastViewedAt);
  const usingTool =
    isChatStatusBusy(thread?.status) &&
    isTurnUsingTool(threadMessages, thread?.status);
  return {
    activity: birdActivityFor(thread?.status, {
      isStarting,
      hasReply: isUnread,
      usingTool,
    }),
    isUnread,
    lastMessageIso,
  };
};
